import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useNavigate } from 'react-router-dom';
import { useToast } from './use-toast';
import { useLanguage } from '@/contexts/LanguageContext';

interface UseAutoLogoutOptions {
  timeoutMinutes?: number;
  warningMinutes?: number;
  onLogout?: () => void;
}

export const useAutoLogout = (options: UseAutoLogoutOptions = {}) => {
  const { timeoutMinutes = 30, warningMinutes = 5, onLogout } = options;
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const isWarningShownRef = useRef<boolean>(false);

  const resetTimer = useCallback(() => {
    // Limpar timers existentes
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }

    // Resetar estado de warning
    isWarningShownRef.current = false;

    // Atualizar última atividade
    lastActivityRef.current = Date.now();

    if (!user) return;

    // Timer de warning (5 minutos antes do logout)
    const warningTime = (timeoutMinutes - warningMinutes) * 60 * 1000;
    warningTimeoutRef.current = setTimeout(() => {
      if (isWarningShownRef.current) return;
      
      isWarningShownRef.current = true;
      toast({
        title: "⚠️ Sessão Expirando",
        description: `Você será desconectado em ${warningMinutes} minutos por inatividade. Clique em qualquer lugar para continuar.`,
        variant: "destructive",
        duration: 10000, // 10 segundos
      });
    }, warningTime);

    // Timer principal de logout
    const logoutTime = timeoutMinutes * 60 * 1000;
    timeoutRef.current = setTimeout(async () => {
      try {
        toast({
          title: "🔐 Sessão Expirada",
          description: "Você foi desconectado por inatividade. Faça login novamente.",
          variant: "destructive",
        });

        // Executar callback personalizado se fornecido
        if (onLogout) {
          onLogout();
        }

        // Fazer logout
        await signOut();
        
        // Redirecionar para login
        navigate('/auth');
      } catch (error) {
        console.error('❌ Erro durante auto-logout:', error);
      }
    }, logoutTime);
  }, [user, timeoutMinutes, warningMinutes, signOut, navigate, toast, onLogout]);

  const handleActivity = useCallback(() => {
    // Se já mostrou o warning, resetar o timer
    if (isWarningShownRef.current) {
      isWarningShownRef.current = false;
      toast({
        title: "✅ Sessão Renovada",
        description: "Sua sessão foi renovada. Continue estudando!",
        variant: "default",
        duration: 3000,
      });
    }
    
    resetTimer();
  }, [resetTimer, toast]);

  // Configurar listeners de atividade
  useEffect(() => {
    if (!user) return;

    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'focus'
    ];

    // Adicionar listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Iniciar timer
    resetTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
    };
  }, [user, handleActivity, resetTimer]);

  // Limpar timers quando usuário faz logout manual
  useEffect(() => {
    if (!user) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
      isWarningShownRef.current = false;
    }
  }, [user]);

  return {
    resetTimer: handleActivity,
    isWarningShown: isWarningShownRef.current,
  };
};
